import { DateTime, DurationObjectUnits } from 'luxon';
import React, { useEffect, useState } from 'react';
import { FinishStampModal } from '../Modal/FinishStampModal';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';
import { createErrorNotification } from '@/helper/ApiHelper';
import { StampService } from '@/services/StampService';
import { useSelector } from 'react-redux';

export const PunchClock = () => {
  const { t } = useTranslation();

  const selectedAffiliate = useSelector((state: any) => state.affiliate.selectedAffiliate);

  const [currentTime, setCurrentTime] = useState(DateTime.local());
  const [isFinishStampModalOpen, setIsFinishStampModalOpen] = useState(false);
  const [punchClockLoading, setPunchClockLoading] = useState(false);
  const [punchClockData, setPunchClockData] = useState({
    start: null,
    duration: 0,
  });

  useEffect(() => {
    getStampInfo();

    const intervalId = setInterval(() => {
      setCurrentTime(DateTime.local());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const getStampInfo = () => {
    setPunchClockLoading(true);

    StampService.getLast(selectedAffiliate.id)
      .then((res: any) => {
        if (res.stamp !== null) {
          setPunchClockData({
            ...punchClockData,
            start: DateTime.fromISO(res.stamp),
          });
        }
      })
      .catch(createErrorNotification)
      .finally(() => setPunchClockLoading(false));
  };

  const openFinishModal = () => {
    setIsFinishStampModalOpen(true);
  };

  const startClock = () => {
    if (punchClockLoading) {
      return;
    }
    setPunchClockLoading(true);

    StampService.stamp(selectedAffiliate.id)
      .then((res: any) => {
        if (res.stamp !== null) {
          setPunchClockData({
            ...punchClockData,
            start: DateTime.fromISO(res.stamp),
          });
        }
      })
      .catch((err) => {
        createErrorNotification(err);
        if (err?.response?.data?.message === 'OVERLAPPING_SHIFT') {
          setPunchClockData({
            ...punchClockData,
            start: null,
          });
        }
      })
      .finally(() => setPunchClockLoading(false));
  };

  const clearClock = () => {
    setPunchClockData({
      duration: 0,
      start: null,
    });
  };

  let clockDiff = {
    hours: 0,
    minutes: 0,
    seconds: 0,
  } as DurationObjectUnits;
  let clockIsTicking = punchClockData.start !== null;
  if (clockIsTicking) {
    const diff = currentTime.diff(punchClockData.start, ['hours', 'minutes', 'seconds']);
    clockDiff = diff.toObject();
  }

  // Helper function to format the time
  function formatTime(timeObject) {
    const hours = String(timeObject.hours).padStart(2, '0');
    const minutes = String(timeObject.minutes).padStart(2, '0');
    const seconds = String(Math.round(timeObject.seconds)).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  return (
    <>
      <div className={'card border'}>
        <div className={'card-body p-6 space-y-2'}>
          <div className="flex items-center">
            <h2 className={'card-title'}>{t('time_tracking.punch_clock')}</h2>
            {clockIsTicking && (
              <span className="relative flex h-3 w-3 ml-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
            )}
          </div>
          <div className="text-center">
            {clockIsTicking && (
              <span className="font-bold text-2xl md:text-3xl">{formatTime(clockDiff)}</span>
            )}
            {!clockIsTicking && <span className="font-bold text-2xl md:text-3xl">00:00:00</span>}
          </div>

          <div className="w-full">
            {!clockIsTicking && (
              <Button
                variant={'base'}
                disabled={punchClockLoading}
                className={'btn btn-block btn-base-100'}
                onClick={() => startClock()}
              >
                {punchClockLoading && <span className="loading loading-spinner loading-xs"></span>}{' '}
                {t('time_tracking.start')}
              </Button>
            )}
            {clockIsTicking && (
              <Button
                variant={'error'}
                className={'btn btn-block btn-error'}
                onClick={() => openFinishModal()}
              >
                {punchClockLoading && <span className="loading loading-spinner loading-xs"></span>}{' '}
                {t('time_tracking.stop')}
              </Button>
            )}
          </div>
        </div>
      </div>
      <FinishStampModal
        isModalOpen={isFinishStampModalOpen}
        setIsModalOpen={setIsFinishStampModalOpen}
        clearClock={clearClock}
      />
    </>
  );
};
